/**
 * 🛡️ G2eGente - Dashboard do Candidato (Web)
 * Idêntico ao app mobile
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import CandidateLayout from '../../components/CandidateLayout';
import { candidatesAPI, applicationsAPI, solicitationsAPI } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  Briefcase, 
  FileText, 
  MessageSquare,
  Users,
  Loader2,
  AlertCircle,
  Bell,
  Brain,
  ChevronRight,
  User,
  Settings
} from 'lucide-react';
import { toast } from 'sonner';

export default function CandidateDashboard() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [stats, setStats] = useState({ 
    applications: 0, 
    pendingSolicitations: 0,
    conversations: 0 
  });
  const [loading, setLoading] = useState(true);
  const [needsSetup, setNeedsSetup] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const profileRes = await candidatesAPI.getMe();
      setProfile(profileRes.data);
      
      const [applicationsRes, solicitationsRes] = await Promise.all([
        applicationsAPI.getMy(),
        solicitationsAPI.getReceived()
      ]);
      
      setStats({
        applications: applicationsRes.data.length,
        pendingSolicitations: solicitationsRes.data.filter(s => s.status === 'PENDING').length,
        conversations: solicitationsRes.data.filter(s => s.status === 'APPROVED').length
      });
    } catch (error) {
      if (error.response?.status === 404) {
        setNeedsSetup(true);
      } else {
        toast.error('Erro ao carregar dados');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <CandidateLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
        </div>
      </CandidateLayout>
    );
  }

  if (needsSetup) {
    return (
      <CandidateLayout>
        <div className="max-w-md mx-auto text-center py-12">
          <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="w-10 h-10 text-amber-500" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-2">
            Complete seu Perfil
          </h2>
          <p className="text-slate-600 mb-6">
            Para acessar todas as funcionalidades, você precisa completar seu perfil profissional.
          </p>
          <Button 
            onClick={() => navigate('/candidate/setup')}
            className="bg-cyan-600 hover:bg-cyan-700"
          >
            Completar Perfil
          </Button>
        </div>
      </CandidateLayout>
    );
  }

  return (
    <CandidateLayout>
      <div className="max-w-4xl mx-auto space-y-6 pb-20 lg:pb-0" data-testid="candidate-dashboard">
        {/* Welcome Header */}
        <div className="bg-gradient-to-r from-slate-900 to-slate-800 rounded-2xl p-6 text-white">
          <h1 className="text-2xl font-bold mb-1">
            Olá, {profile?.nome?.split(' ')[0] || 'Candidato'}! 👋
          </h1>
          <p className="text-slate-300">
            Gerencie seu perfil e acompanhe suas oportunidades
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-3 gap-4">
          <Card 
            className="border-slate-200 cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => navigate('/candidate/applications')}
          >
            <CardContent className="p-4 text-center">
              <div className="w-12 h-12 bg-cyan-100 rounded-xl flex items-center justify-center mx-auto mb-2">
                <FileText className="w-6 h-6 text-cyan-600" />
              </div>
              <p className="text-2xl font-bold text-slate-900">{stats.applications}</p>
              <p className="text-xs text-slate-600">Candidaturas</p>
            </CardContent>
          </Card>

          <Card 
            className="border-slate-200 cursor-pointer hover:shadow-md transition-shadow relative"
            onClick={() => navigate('/candidate/solicitations')}
          >
            <CardContent className="p-4 text-center">
              {stats.pendingSolicitations > 0 && (
                <Badge className="absolute top-2 right-2 bg-red-500">
                  {stats.pendingSolicitations}
                </Badge>
              )}
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center mx-auto mb-2">
                <Users className="w-6 h-6 text-amber-600" />
              </div>
              <p className="text-2xl font-bold text-slate-900">{stats.pendingSolicitations}</p>
              <p className="text-xs text-slate-600">Solicitações</p>
            </CardContent>
          </Card>

          <Card 
            className="border-slate-200 cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => navigate('/candidate/chats')}
          >
            <CardContent className="p-4 text-center">
              <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center mx-auto mb-2">
                <MessageSquare className="w-6 h-6 text-emerald-600" />
              </div>
              <p className="text-2xl font-bold text-slate-900">{stats.conversations}</p>
              <p className="text-xs text-slate-600">Conversas</p>
            </CardContent>
          </Card>
        </div>

        {/* DISC Profile Card */}
        {profile?.disc_profile && (
          <Card className="border-purple-200 bg-purple-50">
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-purple-100 rounded-xl flex items-center justify-center">
                  <Brain className="w-7 h-7 text-purple-600" />
                </div>
                <div className="flex-1">
                  <p className="text-sm text-purple-600 font-medium">Seu Perfil DISC</p>
                  <p className="text-xl font-bold text-purple-900">{profile.disc_profile}</p>
                  {profile.disc_detalhado?.aderencia_cargo && (
                    <p className="text-xs text-purple-600">
                      Aderência: {profile.disc_detalhado.aderencia_cargo}%
                    </p>
                  )}
                </div>
                <Badge className="bg-purple-600">Completo</Badge>
              </div>
            </CardContent>
          </Card>
        )}

        {!profile?.disc_profile && (
          <Card 
            className="border-purple-200 bg-purple-50 cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => navigate('/candidate/disc')}
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-purple-100 rounded-xl flex items-center justify-center">
                  <Brain className="w-7 h-7 text-purple-600" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-purple-900">Complete seu Teste DISC</p>
                  <p className="text-sm text-purple-600">
                    Descubra seu perfil comportamental
                  </p>
                </div>
                <ChevronRight className="w-5 h-5 text-purple-400" />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Quick Menu */}
        <div className="space-y-2">
          <h2 className="text-lg font-semibold text-slate-900 mb-3">Menu Rápido</h2>
          
          <Card 
            className="border-slate-200 cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => navigate('/candidate/jobs')}
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-cyan-100 rounded-xl flex items-center justify-center">
                  <Briefcase className="w-6 h-6 text-cyan-600" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-slate-900">Explorar Vagas</p>
                  <p className="text-sm text-slate-600">Encontre oportunidades para você</p>
                </div>
                <ChevronRight className="w-5 h-5 text-slate-400" />
              </div>
            </CardContent>
          </Card>

          <Card 
            className="border-slate-200 cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => navigate('/candidate/applications')}
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                  <FileText className="w-6 h-6 text-emerald-600" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-slate-900">Minhas Candidaturas</p>
                  <p className="text-sm text-slate-600">Acompanhe suas aplicações</p>
                </div>
                <ChevronRight className="w-5 h-5 text-slate-400" />
              </div>
            </CardContent>
          </Card>

          <Card 
            className="border-slate-200 cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => navigate('/candidate/solicitations')}
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                  <Users className="w-6 h-6 text-amber-600" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-slate-900">Solicitações de Contato</p>
                  <p className="text-sm text-slate-600">Empresas interessadas em você</p>
                </div>
                {stats.pendingSolicitations > 0 && (
                  <Badge className="bg-red-500 text-white">
                    {stats.pendingSolicitations}
                  </Badge>
                )}
                <ChevronRight className="w-5 h-5 text-slate-400" />
              </div>
            </CardContent>
          </Card>

          <Card 
            className="border-slate-200 cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => navigate('/candidate/chats')}
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <MessageSquare className="w-6 h-6 text-blue-600" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-slate-900">Mensagens</p>
                  <p className="text-sm text-slate-600">Converse com empresas</p>
                </div>
                <ChevronRight className="w-5 h-5 text-slate-400" />
              </div>
            </CardContent>
          </Card>

          <Card 
            className="border-slate-200 cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => navigate('/candidate/profile')}
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center">
                  <User className="w-6 h-6 text-slate-600" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-slate-900">Meu Perfil</p>
                  <p className="text-sm text-slate-600">Atualize suas informações</p>
                </div>
                <ChevronRight className="w-5 h-5 text-slate-400" />
              </div>
            </CardContent>
          </Card>

          <Card 
            className="border-slate-200 cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => navigate('/candidate/disc')}
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                  <Brain className="w-6 h-6 text-purple-600" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-slate-900">Teste DISC</p>
                  <p className="text-sm text-slate-600">Avaliação comportamental</p>
                </div>
                <ChevronRight className="w-5 h-5 text-slate-400" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Privacy Notice */}
        <Card className="border-cyan-200 bg-cyan-50">
          <CardContent className="p-4 text-center">
            <p className="text-sm text-cyan-700">
              🔒 Seus dados pessoais são <strong>invisíveis</strong> para empresas até que você aprove o acesso.
            </p>
          </CardContent>
        </Card>
      </div>
    </CandidateLayout>
  );
}
